import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  EmptyView,
  HStack,
  MixpanelEvent,
  MixpanelEventProperty,
  Tab,
  TabAppearance,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerByType,
  useAddTabPopover,
  useCallbackRef,
  useDynamicCallback,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type UseTabsProps,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { PositionsDerivativesBlotter } from './Derivatives/PositionsDerivativesBlotter';
import { PositionsSpotBlotter } from './Spot/PositionsSpotBlotter';
import { PositionsSubAccountsBlotter } from './SubAccounts/PositionsSubAccountsBlotter';
import { PositionsUnifiedBlotter } from './Unified/PositionsUnifiedBlotter';
import {
  POSITIONS_TABS_EXCLUDED_IDS,
  defaultDerivativesBlotterTab,
  defaultSpotBlotterTab,
  defaultSubAccountsBlotterTab,
  defaultUnifiedBlotterTab,
  useDefaultPositionsTabs,
  useEnabledPositionsTabTypes,
} from './defaults';
import { POSITIONS_V3_BLOTTER_PREFIX } from './tokens';
import type { PositionsTabProps, PositionsTabType } from './types';

const tabLabeler: UseTabsProps<PositionsTabProps>['tabLabeler'] = tabLabelerByType<PositionsTabProps, 'tabType'>({
  typeKey: 'tabType',
  typeToLabel: item => item,
});

function getBlotterID(tabID: string | undefined) {
  return `${POSITIONS_V3_BLOTTER_PREFIX}/${tabID}`;
}

export function PositionsV3() {
  const mixpanel = useMixpanel();

  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useDynamicCallback(() => setInitialFiltersOpen(false));
  const handleAdd = useDynamicCallback(() => setInitialFiltersOpen(true));

  const tabTypeOptions = useEnabledPositionsTabTypes();
  const defaultPositionsTabs = useDefaultPositionsTabs();

  const persistedTabs = usePersistedTabs<PositionsTabProps>(POSITIONS_V3_BLOTTER_PREFIX, {
    defaultInitialItems: defaultPositionsTabs,
    defaultInitialSelectedIndex: 0,
    excludedIDs: POSITIONS_TABS_EXCLUDED_IDS,
    onSelect: handleSelect,
  });

  const filteredInitialItems = useMemo(() => {
    return persistedTabs.initialItems.filter(tab => tabTypeOptions.includes(tab.tabType));
  }, [persistedTabs.initialItems, tabTypeOptions]);

  const initialSelectedIndex = useMemo(() => {
    const selectedTab = persistedTabs.initialItems[persistedTabs.initialSelectedIndex];
    if (!selectedTab || !tabTypeOptions.includes(selectedTab.tabType)) {
      return 0;
    }
    const indexInFiltered = filteredInitialItems.findIndex(t => t.id === selectedTab.id);
    return indexInFiltered >= 0 ? indexInFiltered : 0;
  }, [persistedTabs.initialItems, persistedTabs.initialSelectedIndex, filteredInitialItems, tabTypeOptions]);

  // `useTabs` re-fires when the onSelect callback identity changes, so this wrapper must stay stable
  // while still seeing the latest mixpanel instance and persisted tab handlers.
  const onTabSelected = useCallbackRef<NonNullable<UseTabsProps<PositionsTabProps>['onSelect']>>(
    (index: number, tabs: PositionsTabProps[]) => {
      mixpanel.track(MixpanelEvent.SetPositionBlotterTab, {
        [MixpanelEventProperty.TabIndex]: index,
        [MixpanelEventProperty.TabLabel]: tabs?.[index]?.label ?? '',
        [MixpanelEventProperty.TabType]: tabs?.[index]?.tabType ?? 'unknown',
      });
      persistedTabs.onSelect(index, tabs);
    }
  ) satisfies UseTabsProps['onSelect'];

  const tabs = useTabs<PositionsTabProps>({
    ...persistedTabs,
    initialItems: filteredInitialItems,
    initialSelectedIndex,
    showAddTab: tabTypeOptions.length > 0,
    tabLabeler,
    onAdd: handleAdd,
    onSelect: onTabSelected,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useDynamicCallback(() => {
    tabs.clone(tabs.selectedIndex, {
      closable: true,
      reorderable: true,
    });
  });

  const handleUpdateTab = useDynamicCallback((updatedTab: PositionsTabProps) => {
    tabs.updateItem(updatedTab);
  });

  const handleNewTabTypeSelection = useDynamicCallback((tabType: PositionsTabType) => {
    const defaultNewTabProperties =
      tabType === 'Spot'
        ? defaultSpotBlotterTab
        : tabType === 'Derivatives'
          ? defaultDerivativesBlotterTab
          : tabType === 'Sub Accounts'
            ? defaultSubAccountsBlotterTab
            : defaultUnifiedBlotterTab;
    tabs.onAdd?.(defaultNewTabProperties);
    closeAddTabPopover();
  });

  const { renderAddTab, close: closeAddTabPopover } = useAddTabPopover({
    options: tabTypeOptions,
    getLabel: identity,
    size: TabSize.Large,
    onSelection: handleNewTabTypeSelection,
    appearance: TabAppearance.Underlined,
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList
        renderAddTab={renderAddTab}
        isBordered
        rightItems={
          <HStack gap="spacing12">
            <Box ref={filtersContainerRef} />
          </HStack>
        }
        data-testid="positions-blotter-tablist"
      >
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.length === 0 ? (
          <EmptyView flex={1} p="spacing16">
            <Box textAlign="center">
              Contact Talos Support to enable Spot, Derivative or Unified position views.
              <br />A Sub Account position breakdown will appear once Sub Accounts are configured.
            </Box>
          </EmptyView>
        ) : (
          tabs.items.map(tab => (
            <AccordionGroup key={tab.id}>
              {tab.tabType === 'Spot' ? (
                <PositionsSpotBlotter
                  blotterID={`${POSITIONS_V3_BLOTTER_PREFIX}/${tab.id}`}
                  tab={tab}
                  onUpdateTab={handleUpdateTab}
                  onCloneTab={handleCloneTab}
                  initialIsOpen={initialFiltersOpen}
                />
              ) : tab.tabType === 'Derivatives' ? (
                <PositionsDerivativesBlotter
                  blotterID={`${POSITIONS_V3_BLOTTER_PREFIX}/${tab.id}`}
                  tab={tab}
                  onUpdateTab={handleUpdateTab}
                  onCloneTab={handleCloneTab}
                  initialIsOpen={initialFiltersOpen}
                />
              ) : tab.tabType === 'Sub Accounts' ? (
                <PositionsSubAccountsBlotter
                  blotterID={`${POSITIONS_V3_BLOTTER_PREFIX}/${tab.id}`}
                  tab={tab}
                  onUpdateTab={handleUpdateTab}
                  onCloneTab={handleCloneTab}
                  initialIsOpen={initialFiltersOpen}
                />
              ) : (
                <PositionsUnifiedBlotter
                  blotterID={`${POSITIONS_V3_BLOTTER_PREFIX}/${tab.id}`}
                  tab={tab}
                  onUpdateTab={handleUpdateTab}
                  onCloneTab={handleCloneTab}
                  initialIsOpen={initialFiltersOpen}
                />
              )}
            </AccordionGroup>
          ))
        )}
      </TabPanels>
    </Tabs>
  );
}
